import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STUDENT_ID_PATTERN } from '@mdt/contracts';
import { Alert, Button, Dialog, ErrorAlert, KeyedList, Loading, RadioGroup, TextArea, TextField } from '../components/kit.tsx';
import { command, query } from '../lib/api.ts';
import { useFormat, usePageTitle, usePersonName } from '../lib/format.ts';
import { useSignedIn } from '../lib/session.tsx';

type Member = {
  membershipId: string; personId: string; studentId: string | null; nameTh: string; nameEn: string | null;
  role: 'leader' | 'member'; state: 'invited' | 'active' | 'suspended'; invitedAt: string; joinedAt: string | null;
};
type Names = { nameTh: string; nameEn: string | null };
export type Mine = {
  cohort: { cohortId: string; code: string; labelTh: string; labelEn: string | null; state: string; phase: string; formationOpen: boolean } | null;
  project: {
    projectId: string; projectCode: string; state: string; maxMembers: number; titleTh: string | null; titleEn: string | null;
    myRole: 'leader' | 'member'; members: Member[];
  } | null;
  invitations: {
    membershipId: string; projectCode: string; invitedBy: Names; leader: Names | null; liveMembers: number; maxMembers: number;
    invitedAt: string; expiresAt: string;
  }[];
};

const MINE = ['project.mine'] as const;

/**
 * "My group" (roadmap 4.2): found a group as its leader, invite classmates by student ID, answer
 * invitations (accepting one declines the rest), hand over the lead, leave. Everything here stops at the
 * formation lock; from then on only an administrator changes groups.
 */
export function Group() {
  const { t } = useTranslation();
  usePageTitle(t('group.title'));
  const mine = useQuery({ queryKey: MINE, queryFn: () => query<Mine>('project.mine') });
  const [notice, setNotice] = useState<string | null>(null);

  if (mine.isPending) return <Loading />;
  if (mine.error) return <div className="stack"><h1>{t('group.title')}</h1><ErrorAlert error={mine.error} /></div>;
  const { cohort, project, invitations } = mine.data;
  return (
    <div className="stack">
      <h1>{t('group.title')}</h1>
      {notice ? <Alert tone="success"><p>{notice}</p></Alert> : null}
      {!cohort ? <Alert><p>{t('group.notEnrolled')}</p></Alert> : (
        <>
          <p className="muted">{t('group.cohortLine', { code: cohort.code })}</p>
          {!cohort.formationOpen ? (
            <Alert tone="warning"><p>{cohort.state === 'active' ? t('group.closed') : t('group.notOpen')}</p></Alert>
          ) : null}
          {project
            ? <OwnGroup project={project} open={cohort.formationOpen} onNotice={setNotice} />
            : <NoGroup cohortId={cohort.cohortId} open={cohort.formationOpen} invitations={invitations} onNotice={setNotice} />}
        </>
      )}
    </div>
  );
}

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: MINE });
}

function NoGroup({ cohortId, open, invitations, onNotice }: { cohortId: string; open: boolean; invitations: Mine['invitations']; onNotice(s: string): void }) {
  const { t } = useTranslation();
  const f = useFormat();
  const name = usePersonName();
  const refresh = useRefresh();
  const [confirm, setConfirm] = useState(false);
  const create = useMutation({
    mutationFn: () => command<{ projectCode: string }>('project.create', { cohortId }),
    onSuccess: async (r) => {
      setConfirm(false);
      onNotice(t('group.created', { code: r.result.projectCode }));
      await refresh();
    },
  });
  const respond = useMutation({
    mutationFn: (v: { membershipId: string; accept: boolean }) => command('project.respondInvite', v),
    onSuccess: async (_r, v) => {
      onNotice(v.accept ? t('group.joined') : t('group.declined'));
      await refresh();
    },
  });
  return (
    <>
      <section className="card" aria-labelledby="group-invitations">
        <h2 id="group-invitations">{t('group.invitations')}</h2>
        <ErrorAlert error={respond.error} />
        {invitations.length === 0 ? <p>{t('group.noInvitations')}</p> : (
          <KeyedList items={invitations} getKey={(i) => i.membershipId} label={t('group.invitations')}>
            {(i) => (
              <div className="row" data-testid="invitation">
                <span>
                  <strong>{i.projectCode}</strong>{' '}
                  {i.leader ? t('group.ledBy', { name: name(i.leader) }) : null}
                  <br />
                  <span className="muted small">
                    {t('group.invitedBy', { name: name(i.invitedBy) })} · {t('group.size', { count: i.liveMembers, max: i.maxMembers })} ·{' '}
                    {t('group.expires', { at: f.dateTime(i.expiresAt) })}
                  </span>
                </span>
                <span className="row">
                  <Button disabled={!open} busy={respond.isPending && respond.variables?.membershipId === i.membershipId && respond.variables.accept}
                    aria-label={t('group.acceptLabel', { code: i.projectCode })}
                    onClick={() => respond.mutate({ membershipId: i.membershipId, accept: true })}>{t('group.accept')}</Button>
                  <Button variant="secondary" disabled={!open}
                    busy={respond.isPending && respond.variables?.membershipId === i.membershipId && !respond.variables.accept}
                    aria-label={t('group.declineLabel', { code: i.projectCode })}
                    onClick={() => respond.mutate({ membershipId: i.membershipId, accept: false })}>{t('group.decline')}</Button>
                </span>
              </div>
            )}
          </KeyedList>
        )}
        {invitations.length > 1 ? <p className="muted small">{t('group.acceptOneNote')}</p> : null}
      </section>
      <section className="card" aria-labelledby="group-create">
        <h2 id="group-create">{t('group.createTitle')}</h2>
        <p>{t('group.createBody')}</p>
        <Button disabled={!open} onClick={() => setConfirm(true)}>{t('group.create')}</Button>
        <Dialog open={confirm} title={t('group.createTitle')} onCancel={() => setConfirm(false)} actions={(
          <>
            <Button busy={create.isPending} onClick={() => create.mutate()}>{t('group.createConfirm')}</Button>
            <Button variant="secondary" onClick={() => setConfirm(false)}>{t('common.cancel')}</Button>
          </>
        )}>
          <p>{t('group.createWarning')}</p>
          <ErrorAlert error={create.error} />
        </Dialog>
      </section>
    </>
  );
}

function OwnGroup({ project, open, onNotice }: { project: NonNullable<Mine['project']>; open: boolean; onNotice(s: string): void }) {
  const { t, i18n } = useTranslation();
  const f = useFormat();
  const name = usePersonName();
  const { view } = useSignedIn();
  const refresh = useRefresh();
  const me = view.person.personId;
  const live = project.members.filter((m) => m.state !== 'invited');
  const invited = project.members.filter((m) => m.state === 'invited');
  const leader = project.myRole === 'leader';
  const forming = project.state === 'forming';
  const canChange = open && forming;
  const full = live.length >= project.maxMembers;
  const title = i18n.language !== 'th' && project.titleEn ? project.titleEn : project.titleTh;

  const cancel = useMutation({
    mutationFn: (membershipId: string) => command('project.cancelInvite', { membershipId }),
    onSuccess: async () => {
      onNotice(t('group.inviteCancelled'));
      await refresh();
    },
  });
  return (
    <>
      <section className="card card--emphasis" aria-labelledby="group-own">
        <h2 id="group-own">
          <span data-testid="project-code">{project.projectCode}</span>{' '}
          <span className="tag">{t(`group.states.${project.state}`)}</span>
        </h2>
        {title ? <p>{title}</p> : null}
        <p className="muted">{t('group.size', { count: live.length, max: project.maxMembers })}</p>
        <ErrorAlert error={cancel.error} />
        <KeyedList items={[...live, ...invited]} getKey={(m) => m.membershipId} label={t('group.members')}>
          {(m) => (
            <div className="row" data-testid="member">
              <span>
                <strong>{name(m)}</strong>{m.personId === me ? ` (${t('group.you')})` : null}{' '}
                {m.role === 'leader' && m.state !== 'invited' ? <span className="tag">{t('group.leader')}</span> : null}{' '}
                {m.state === 'invited' ? <span className="tag">{t('group.invitedTag')}</span> : null}{' '}
                {m.state === 'suspended' ? <span className="tag">{t('group.suspendedTag')}</span> : null}
                <br />
                <span className="muted small">
                  {m.studentId}
                  {m.state === 'invited' ? ` · ${t('group.invitedOn', { at: f.dateTime(m.invitedAt) })}` : null}
                </span>
              </span>
              {m.state === 'invited' && canChange ? (
                <Button variant="secondary" busy={cancel.isPending && cancel.variables === m.membershipId}
                  aria-label={t('group.cancelInviteLabel', { name: name(m) })}
                  onClick={() => cancel.mutate(m.membershipId)}>{t('group.cancelInvite')}</Button>
              ) : null}
            </div>
          )}
        </KeyedList>
      </section>
      {canChange && !full ? <InviteForm projectId={project.projectId} onNotice={onNotice} /> : null}
      {canChange && full ? <p className="muted">{t('group.fullNote')}</p> : null}
      {canChange ? (
        <section className="card" aria-labelledby="group-manage">
          <h2 id="group-manage">{t('group.manage')}</h2>
          <div className="row">
            {leader && live.length > 1 ? <TransferLead project={project} candidates={live.filter((m) => m.personId !== me && m.state === 'active')} onNotice={onNotice} /> : null}
            <Leave projectId={project.projectId} blocked={leader && live.length > 1} last={live.length === 1} onNotice={onNotice} />
          </div>
          {leader && live.length > 1 ? <p className="muted small">{t('group.transferFirst')}</p> : null}
        </section>
      ) : null}
    </>
  );
}

function InviteForm({ projectId, onNotice }: { projectId: string; onNotice(s: string): void }) {
  const { t } = useTranslation();
  const refresh = useRefresh();
  const [studentId, setStudentId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: (id: string) => command('project.invite', { projectId, inviteeStudentId: id }),
    onSuccess: async () => {
      setStudentId('');
      onNotice(t('group.invited'));
      await refresh();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const id = studentId.trim();
    if (!STUDENT_ID_PATTERN.test(id)) {
      setError(t('group.studentIdInvalid'));
      return;
    }
    setError(null);
    invite.mutate(id);
  };
  return (
    <section className="card" aria-labelledby="group-invite">
      <h2 id="group-invite">{t('group.inviteTitle')}</h2>
      <form onSubmit={submit} noValidate>
        <TextField label={t('group.inviteLabel')} hint={t('group.inviteHint')} value={studentId} inputMode="numeric" autoComplete="off"
          error={error} required onChange={(e) => setStudentId(e.currentTarget.value)} />
        <ErrorAlert error={invite.error} />
        <Button type="submit" busy={invite.isPending}>{t('group.invite')}</Button>
      </form>
    </section>
  );
}

function TransferLead({ project, candidates, onNotice }: { project: NonNullable<Mine['project']>; candidates: Member[]; onNotice(s: string): void }) {
  const { t } = useTranslation();
  const name = usePersonName();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(candidates[0]?.personId ?? '');
  const transfer = useMutation({
    mutationFn: () => command('project.transferLead', { projectId: project.projectId, newLeaderPersonId: to }),
    onSuccess: async () => {
      setOpen(false);
      onNotice(t('group.transferred'));
      await refresh();
    },
  });
  if (candidates.length === 0) return null;
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>{t('group.transfer')}</Button>
      <Dialog open={open} title={t('group.transfer')} onCancel={() => setOpen(false)} actions={(
        <>
          <Button busy={transfer.isPending} disabled={!to} onClick={() => transfer.mutate()}>{t('group.transferConfirm')}</Button>
          <Button variant="secondary" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
        </>
      )}>
        <RadioGroup legend={t('group.transferTo')} name="new-leader" value={to}
          options={candidates.map((m) => ({ value: m.personId, label: name(m) }))} onChange={setTo} />
        <ErrorAlert error={transfer.error} />
      </Dialog>
    </>
  );
}

function Leave({ projectId, blocked, last, onNotice }: { projectId: string; blocked: boolean; last: boolean; onNotice(s: string): void }) {
  const { t } = useTranslation();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const leave = useMutation({
    mutationFn: () => command<{ dissolved: boolean }>('project.leave', { projectId, reason: reason.trim() }),
    onSuccess: async (r) => {
      setOpen(false);
      setReason('');
      onNotice(r.result.dissolved ? t('group.dissolved') : t('group.left'));
      await refresh();
    },
  });
  const confirm = () => {
    if (!reason.trim()) {
      setError(t('group.reasonRequired'));
      return;
    }
    setError(null);
    leave.mutate();
  };
  return (
    <>
      <Button variant="danger" disabled={blocked} onClick={() => setOpen(true)}>{t('group.leave')}</Button>
      <Dialog open={open} title={t('group.leave')} onCancel={() => setOpen(false)} actions={(
        <>
          <Button variant="danger" busy={leave.isPending} onClick={confirm}>{t('group.leaveConfirm')}</Button>
          <Button variant="secondary" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
        </>
      )}>
        <p>{last ? t('group.leaveLast') : t('group.leaveBody')}</p>
        <TextArea label={t('group.reason')} value={reason} required error={error} maxLength={2000}
          onChange={(e) => setReason(e.currentTarget.value)} />
        <ErrorAlert error={leave.error} />
      </Dialog>
    </>
  );
}
